/**
 * Per-plugin runtime context — scoped API client, event bus, capabilities
 * and the PluginUIContext — shared by full pages (PluginPageHost) and
 * embedded components (PluginEmbedHost), so a plugin's component gets the
 * same context wherever it is rendered.
 *
 * API clients and event APIs are cached per plugin: stable identities across
 * renders, pages and embeds (avoids re-created API adapters and the StrictMode
 * stale-fetch races documented in PluginPageHost).
 */

import client from '../../api/client';
import { getAllPluginCapabilities } from '../capability-enforcement';
import { usePluginEventBusStore } from '../plugin-event-bus';
import type { ResolvedCapabilities, PluginUIContext as SdkUIContext } from '@neuro/plugin-sdk';

const noop = () => {};
const noopAsync = async () => {};

/** Strict denial sentinel for plugins without registered capabilities. */
export const EMPTY_CAPS: ResolvedCapabilities = {
  granted: new Set(),
  declared: new Set(),
  autoGranted: new Set(),
};

export interface PluginApi {
  get<T = unknown>(url: string, params?: Record<string, unknown>): Promise<T>;
  post<T = unknown>(url: string, data?: unknown): Promise<T>;
  put<T = unknown>(url: string, data?: unknown): Promise<T>;
  patch<T = unknown>(url: string, data?: unknown): Promise<T>;
  delete<T = unknown>(url: string, data?: unknown): Promise<T>;
}

/**
 * Plugin-scoped API client returning raw response data.
 * Absolute URLs and `/api/…` paths pass through; short paths are prefixed
 * with `/api/plugins/<pluginId>`.
 */
export function createPluginApi(pluginId: string | undefined): PluginApi {
  const pluginBase = pluginId ? `/api/plugins/${pluginId}` : '';
  const resolve = (url: string) => {
    const isAbsoluteUrl = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url) || url.startsWith('//');
    if (isAbsoluteUrl || url.startsWith('/api/') || !pluginBase) return url;
    return url.startsWith('/') ? `${pluginBase}${url}` : `${pluginBase}/${url}`;
  };
  return {
    get: <T = unknown>(url: string, params?: Record<string, unknown>): Promise<T> =>
      client.get<T>(resolve(url), params ? { params } : undefined).then((res) => res.data as T),
    post: <T = unknown>(url: string, data?: unknown): Promise<T> => client.post<T>(resolve(url), data).then((res) => res.data as T),
    put: <T = unknown>(url: string, data?: unknown): Promise<T> => client.put<T>(resolve(url), data).then((res) => res.data as T),
    patch: <T = unknown>(url: string, data?: unknown): Promise<T> => client.patch<T>(resolve(url), data).then((res) => res.data as T),
    delete: <T = unknown>(url: string, data?: unknown): Promise<T> =>
      client.delete<T>(resolve(url), data !== undefined ? { data } : undefined).then((res) => res.data as T),
  };
}

const apiCache = new Map<string, PluginApi>();

export function getScopedPluginApi(pluginId: string | undefined): PluginApi {
  const key = pluginId ?? '';
  let api = apiCache.get(key);
  if (!api) {
    api = createPluginApi(pluginId);
    apiCache.set(key, api);
  }
  return api;
}

export interface PluginEventsApi {
  emit(targetPluginIdOrEventName: string, eventNameOrPayload?: any, payload?: any): void;
  on(sourcePluginId: string, eventName: string, handler: (event: any) => void): () => void;
  subscribe(eventType: string, handler: (payload: unknown) => void): () => void;
  publish(eventType: string, payload: unknown): void;
}

const NOOP_EVENTS: PluginEventsApi = {
  emit: noop,
  on: () => noop,
  subscribe: () => noop,
  publish: noop,
};

const eventsCache = new Map<string, PluginEventsApi>();

/**
 * Host-mediated event API for a plugin: both the PluginEventsAPI shape
 * (emit/on) and the PluginUIContext.events shape (subscribe/publish).
 */
export function getPluginEventsApi(pluginId: string | undefined): PluginEventsApi {
  if (!pluginId) return NOOP_EVENTS;
  let events = eventsCache.get(pluginId);
  if (events) return events;
  events = {
    // Supports both (targetPluginId, eventName, payload) and (eventName, payload).
    emit: (targetPluginIdOrEventName, eventNameOrPayload, payload) => {
      const bus = usePluginEventBusStore.getState();
      if (payload !== undefined) bus.emit(pluginId, eventNameOrPayload, payload);
      else bus.emit(pluginId, targetPluginIdOrEventName, eventNameOrPayload);
    },
    on: (sourcePluginId, eventName, handler) => usePluginEventBusStore.getState().on(pluginId, sourcePluginId, eventName, handler),
    // Events of this type from ANY plugin ('*').
    subscribe: (eventType, handler) =>
      usePluginEventBusStore.getState().on(pluginId, '*', eventType, (event: any) => handler(event?.payload ?? event)),
    publish: (eventType, eventPayload) => usePluginEventBusStore.getState().emit(pluginId, eventType, eventPayload),
  };
  eventsCache.set(pluginId, events);
  return events;
}

const warnedMissingCaps = new Set<string>();

/** Registered capabilities, or strict denial (warned once per plugin). */
export function resolvePluginCapabilities(pluginId: string | undefined, source = 'PluginRuntime'): ResolvedCapabilities {
  if (!pluginId) return EMPTY_CAPS;
  const caps = getAllPluginCapabilities().get(pluginId);
  if (caps) return caps;
  if (!warnedMissingCaps.has(pluginId)) {
    warnedMissingCaps.add(pluginId);
    console.warn(
      `[${source}] Plugin "${pluginId}" has no registered capabilities. ` +
      'All capability checks will deny. Ensure the plugin declares capabilities in its manifest.',
    );
  }
  return EMPTY_CAPS;
}

export interface HostContextInput {
  pluginId: string;
  userId: string;
  navigate: (path: string) => void;
  api: PluginApi;
  events: PluginEventsApi;
  embed?: SdkUIContext['embed'];
}

export function buildPluginHostContext({ pluginId, userId, navigate, api, events, embed }: HostContextInput): SdkUIContext {
  const context: SdkUIContext = {
    pluginId,
    version: '1.0.0',
    user: { username: userId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    theme: (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') || 'light',
    navigate,
    openModal: noop,
    closeModal: noop,
    toast: { success: noop, error: noop, info: noop },
    api,
    config: {},
    getConfig: async () => ({}),
    saveConfig: noopAsync,
    storage: {
      get: async () => null,
      set: noopAsync,
      delete: noopAsync,
    },
    events,
  } as SdkUIContext;
  if (embed) context.embed = embed;
  return context;
}

/** Testing only. */
export function __resetPluginRuntimeCaches(): void {
  apiCache.clear();
  eventsCache.clear();
  warnedMissingCaps.clear();
}
